import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { X } from "lucide-react";
import { normalRangeAPI } from "../services/api";

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 10mm 15mm;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      margin: 0;
      padding: 0;
    }

    body * {
      visibility: hidden;
    }

    #health-report-modal,
    #health-report-modal * {
      visibility: visible;
    }

    #health-report-modal {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;

  @media print {
    background-color: white;
    position: static;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 210mm;
  height: 297mm;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

  @media print {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: none;
    box-shadow: none;
    overflow: visible;
    border-radius: 0;
    margin: 0;
    padding: 0;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 6vw;
  background: white;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;

  &:hover {
    background-color: #f3f4f6;
  }

  @media print {
    display: none;
  }
`;

const PrintButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 12vw;
  background: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  font-size: 14px;
  font-weight: 500;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background-color: ${props => props.disabled ? '#9CA3AF' : '#0891B2'};
  }

  @media print {
    display: none;
  }
`;

const ReportContainer = styled.div`
  padding: 2rem;
  font-family: 'Sarabun', sans-serif;
  overflow: visible;

  @media print {
    padding: 0;
    width: 100%;
    box-sizing: border-box;
    transform: none;
  }
`;

const PageNumber = styled.div`
  position: relative;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 1rem;
`;

const PageBreak = styled.div`
  @media screen {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 2px dashed #E5E7EB;
  }

  @media print {
    page-break-after: always;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
`;

const TitleSection = styled.div`
  text-align: right;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
`;

const PatientInfoSection = styled.div`
  margin-bottom: 2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const InfoRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #E5E7EB;
  margin: 1.5rem 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  overflow: visible;
`;

const LeftSection = styled.div`
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HumanImage = styled.img`
  width: 230px;
  height: auto;
  margin-bottom: 1rem;
`;

const RightSection = styled.div`
  flex: 0 0 400px;
  overflow: visible;
`;

const ResultHeader = styled.div`
  background-image: url('/bg_headbar.png');
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  color: #000000;
  padding-top: 0.5rem;
  padding-bottom: 1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 18px;
  font-weight: 600;
  position: relative;
  margin-bottom: -1.5rem;
  margin-left: -3.4%;
  z-index: 9999;
  overflow: visible;
  min-height: 50px;
`;

const ResultTable = styled.div`
  border: 1px solid #E5E7EB;
  border-top: none;
  border-radius: 0 0 0 0;
  overflow: visible;
`;

const ResultRow = styled.div`
  display: flex;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultRowFull = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultFullTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const ResultFullContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  padding-left: 1.5rem;
`;

const ResultLabel = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: ${props => props.bold ? '600' : '400'};
  color: #111827;
  background-color: white;
  text-align: left;
`;

const ResultValue = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: white;
`;

const NoteSection = styled.div`
  margin-top: 1.5rem;
`;

const NoteTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const AdditionalSection = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoBox = styled.div`
  border: 2px solid #67C3F3;
  border-radius: 1rem;
  padding: 1.5rem;
`;

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const InfoIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const InfoBoxContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RiskButton = styled.button`
  background-color: transparent;
  border: 1px solid #67C3F3;
  color: #111827;
  padding: 0.5rem 1.5rem;
  font-size: 14px;
  font-weight: 400;
  cursor: default;
`;

const BloodTestTableWrapper = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const BloodTestTable = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  width: 80%;
`;

const BloodTestHeader = styled.div`
  background-image: url('/bg_headbar.png');
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  color: #000000;
  padding-top: 0.1rem;
  padding-bottom: 1.25rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 16px;
  font-weight: 600;
  width: 40%;
  position: relative;
  margin-bottom: -1.5rem;
  align-self: flex-start;
  margin-left: 8.2%;
  z-index: 9999;
`;

const BloodTestContent = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  overflow: hidden;
  width: 80%;
`;

const BloodTestRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const BloodTestCell = styled.div`
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: ${props => props.header ? '600' : (props.abnormal ? '600' : '400')};
  color: ${props => props.header ? '#111827' : (props.abnormal ? '#DC2626' : '#111827')};
  background-color: white;
  text-align: ${props => props.center ? 'center' : 'left'};

  ${props => props.abnormal && `
    &::before {
      content: '⚠ ';
      font-size: 16px;
    }
  `}
`;

const AlertValue = styled.span`
  color: #DC2626;
  font-weight: 600;

  &::before {
    content: '⚠ ';
  }
`;

const UrineTestNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 14px;
  color: #DC2626;
  width: 100%;
  padding-left: 10%;

  &::before {
    content: '⚠';
    font-size: 16px;
  }
`;

const HealthReportModal = ({ isOpen, onClose, recordData }) => {
  const [normalRanges, setNormalRanges] = useState({});

  // ตรวจสอบว่าสถานะเป็น "approved" หรือไม่
  const isApproved = recordData?.approvalStatus === 'approved';

  // ฟังก์ชันดึงข้อมูลค่าปกติจาก API
  const fetchMissingNormalRanges = async () => {
    if (!recordData?.age || !recordData?.gender) return;

    const sex = recordData.gender === 'ชาย' ? 'M' : 'F';
    const age = parseInt(recordData.age) || 0;

    // รายการพารามิเตอร์ทั้งหมดที่ต้องตรวจสอบ
    const allParameters = [
      'WBC', 'Hemoglobin', 'Hematocrit', 'Platelets', 'Neutrophile', 'Lymphocyte', 'Monocyte',
      'Glucose', 'Protein', 'คอเลสเตอรอลรวม', 'ไตรกลีเซอไรด์', 'HDL', 'LDL',
      'Creatinine', 'BUN, Urea', 'eGFR', 'SGPT (ALT)', 'SGOT (AST)', 'ALP (Alkaline Phosphatase)',
      'Uric Acid', 'Color', 'pH', 'Specific Gravity', 'Ketones', 'Blood', 'Leukocytes',
      'Nitrite', 'Bilirubin', 'Urobilinogen', 'RBC (Urine)', 'WBC (Urine)'
    ];

    // สร้างรายการ request สำหรับดึงค่าปกติ
    const testsForAPI = allParameters.map(paramName => ({
      name: paramName,
      sex: sex,
      age: age
    }));

    try {
      const response = await normalRangeAPI.getBatch(testsForAPI);
      if (response.status === 'success' && response.data) {
        const rangesMap = {};
        response.data.forEach(result => {
          if (result.normal_range && !result.error) {
            rangesMap[result.test_name] = result.normal_range;
          }
        });
        setNormalRanges(rangesMap);
      }
    } catch (error) {
      console.error('Error fetching normal ranges:', error);
    }
  };

  // ดึงข้อมูลค่าปกติจาก API เมื่อ modal เปิดและมี recordData
  useEffect(() => {
    if (isOpen && recordData) {
      fetchMissingNormalRanges();
    }
  }, [isOpen, recordData]);

  // Early return ถ้า modal ไม่เปิด
  if (!isOpen) return null;

  // ฟังก์ชันตรวจสอบว่าค่าผิดปกติหรือไม่
  const isAbnormal = (value, normalRange, unit = '') => {
    if (!value || value === '-' || !normalRange || normalRange === '-') return false;

    // ลบ comma ออกจากตัวเลข (เช่น "6,670" -> "6670")
    const cleanValue = String(value).replace(/,/g, '');
    let numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return false;

    // ✅ แปลงค่าตามหน่วย: ถ้าหน่วยเป็น "×10³" หรือ "1000 cells/µL" หรือ "K/µL" ให้หารด้วย 1000
    if (unit && (unit.includes('×10³') || unit.includes('x10³') || unit.includes('10^3') || unit.includes('1000') || unit.toLowerCase().includes('k/'))) {
      numValue = numValue / 1000;
    }

    // แยกประเภทของ normal range
    // รูปแบบ: "min-max", "<max", ">min", "M x-y, F x-y", "ค่าคงที่"

    // รูปแบบ "M 12.0-18.0, F 12.0-16.0" (ขึ้นกับเพศ)
    if (normalRange.includes('M ') || normalRange.includes('F ')) {
      const gender = recordData?.gender || '';
      if (gender === 'ชาย') {
        const maleMatch = normalRange.match(/M\s+([\d.]+)-([\d.]+)/);
        if (maleMatch) {
          const min = parseFloat(maleMatch[1]);
          const max = parseFloat(maleMatch[2]);
          return numValue < min || numValue > max;
        }
      } else if (gender === 'หญิง') {
        const femaleMatch = normalRange.match(/F\s+([\d.]+)-([\d.]+)/);
        if (femaleMatch) {
          const min = parseFloat(femaleMatch[1]);
          const max = parseFloat(femaleMatch[2]);
          return numValue < min || numValue > max;
        }
      }
    } else if (normalRange.includes('-') && !normalRange.startsWith('<') && !normalRange.startsWith('>')) {
      // รูปแบบ min-max เช่น "4-10" หรือ "5000-10000"
      const parts = normalRange.split('-');
      const min = parseFloat(parts[0].replace(/,/g, '').trim());
      const max = parseFloat(parts[1].replace(/,/g, '').trim());
      if (!isNaN(min) && !isNaN(max)) {
        return numValue < min || numValue > max;
      }
    } else if (normalRange.startsWith('<')) {
      // รูปแบบ <max เช่น "<200"
      const max = parseFloat(normalRange.substring(1).replace(/,/g, '').trim());
      if (!isNaN(max)) {
        return numValue >= max;
      }
    } else if (normalRange.startsWith('>')) {
      // รูปแบบ >min เช่น ">50"
      const min = parseFloat(normalRange.substring(1).replace(/,/g, '').trim());
      if (!isNaN(min)) {
        return numValue <= min;
      }
    } else if (normalRange === 'ไม่มี' || normalRange === 'Not found' || normalRange === 'Negative') {
      // สำหรับค่าที่ต้องเป็น "ไม่มี" แต่มีค่า
      return value !== 'ไม่มี' && value !== 'Not found' && value !== 'Negative' && value !== '-';
    }

    return false;
  };

  // คำนวณ BMI Status
  const getBmiStatus = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return '-';
    if (bmiValue < 18.5) return 'น้ำหนักต่ำกว่าเกณฑ์';
    if (bmiValue < 23) return 'สมส่วน';
    if (bmiValue < 25) return 'น้ำหนักเกิน';
    if (bmiValue < 30) return 'อ้วนเล็กน้อย';
    return 'อ้วนมาก';
  };

  // ดึงข้อมูล parameter จาก parameters array
  const getParameterValue = (name) => {
    const param = recordData?.parameters?.find(p => p.name === name);
    return param?.result || '-';
  };

  const getParameterNormal = (name) => {
    const param = recordData?.parameters?.find(p => p.name === name);
    // ถ้ามีค่าปกติใน recordData ให้ใช้ค่านั้น ไม่เช่นนั้นใช้จาก normalRanges
    if (param?.normal && param.normal !== '-') {
      return param.normal;
    }
    // ดึงจาก normalRanges state ที่ได้จาก API
    return normalRanges[name] || '-';
  };

  const getParameterUnit = (name) => {
    const param = recordData?.parameters?.find(p => p.name === name);
    return param?.unit || '';
  };

  // แปลงข้อมูลจาก recordData
  const data = {
    patientType: "บริษัท ไทยร่วมพัฒนา",
    patientName: recordData?.patientName || "-",
    age: recordData?.age || "-",
    gender: recordData?.gender || "-",
    hn: recordData?.hn || "-",
    date: recordData?.date || "-",
    weight: recordData?.weight ? `${recordData.weight} กิโลกรัม` : "-",
    height: recordData?.height ? `${recordData.height} เซนติเมตร` : "-",
    bmi: recordData?.bmi ? `${recordData.bmi} กก./ม.` : "-",
    bmiStatus: getBmiStatus(recordData?.bmi),
    bloodPressure: recordData?.bloodPressure ? `${recordData.bloodPressure} มม.ปรอก` : "-",
    pulse: recordData?.pulse ? `${recordData.pulse} ครั้ง/นาที` : "-",
    diseases: recordData?.diseases || "ไม่มี",
    doctorNote: recordData?.doctorNote || "-",
    foodAllergies: recordData?.foodAllergies || "-",
    tags: recordData?.tags || [],
    // คำแนะนำจาก AI
    nutritionAdvice: recordData?.nutritionAdvice || "",
    exerciseAdvice: recordData?.exerciseAdvice || "",
    sleepAdvice: recordData?.sleepAdvice || "",
    // ข้อมูล Lab Results - หน้า 2
    wbc: getParameterValue('WBC'),
    wbcNormal: getParameterNormal('WBC'),
    wbcUnit: getParameterUnit('WBC'),
    hemoglobin: getParameterValue('Hemoglobin'),
    hemoglobinNormal: getParameterNormal('Hemoglobin'),
    hemoglobinUnit: getParameterUnit('Hemoglobin'),
    hematocrit: getParameterValue('Hematocrit'),
    hematocritNormal: getParameterNormal('Hematocrit'),
    hematocritUnit: getParameterUnit('Hematocrit'),
    platelet: getParameterValue('Platelets'),
    plateletNormal: getParameterNormal('Platelets'),
    plateletUnit: getParameterUnit('Platelets'),
    neutrophile: getParameterValue('Neutrophile'),
    neutrophileNormal: getParameterNormal('Neutrophile'),
    neutrophileUnit: getParameterUnit('Neutrophile'),
    lymphocyte: getParameterValue('Lymphocyte'),
    lymphocyteNormal: getParameterNormal('Lymphocyte'),
    lymphocyteUnit: getParameterUnit('Lymphocyte'),
    monocyte: getParameterValue('Monocyte'),
    monocyteNormal: getParameterNormal('Monocyte'),
    monocyteUnit: getParameterUnit('Monocyte'),
    // Urinalysis - หน้า 2
    urineSugar: getParameterValue('Glucose'),
    urineSugarNormal: getParameterNormal('Glucose'),
    urineSugarUnit: getParameterUnit('Glucose'),
    urineProtein: getParameterValue('Protein'),
    urineProteinNormal: getParameterNormal('Protein'),
    urineProteinUnit: getParameterUnit('Protein'),
    // ไขมันในเลือด - หน้า 3
    totalCholesterol: getParameterValue('คอเลสเตอรอลรวม'),
    totalCholesterolNormal: getParameterNormal('คอเลสเตอรอลรวม'),
    totalCholesterolUnit: getParameterUnit('คอเลสเตอรอลรวม'),
    triglyceride: getParameterValue('ไตรกลีเซอไรด์'),
    triglycerideNormal: getParameterNormal('ไตรกลีเซอไรด์'),
    triglycerideUnit: getParameterUnit('ไตรกลีเซอไรด์'),
    hdl: getParameterValue('HDL'),
    hdlNormal: getParameterNormal('HDL'),
    hdlUnit: getParameterUnit('HDL'),
    ldl: getParameterValue('LDL'),
    ldlNormal: getParameterNormal('LDL'),
    ldlUnit: getParameterUnit('LDL'),
    // การทำงานของไต - หน้า 3
    creatinine: getParameterValue('Creatinine'),
    creatinineNormal: getParameterNormal('Creatinine'),
    creatinineUnit: getParameterUnit('Creatinine'),
    bun: getParameterValue('BUN, Urea'),
    bunNormal: getParameterNormal('BUN, Urea'),
    bunUnit: getParameterUnit('BUN, Urea'),
    egfr: getParameterValue('eGFR'),
    egfrNormal: getParameterNormal('eGFR'),
    egfrUnit: getParameterUnit('eGFR'),
    // การทำงานของตับ - หน้า 3
    sgpt: getParameterValue('SGPT (ALT)'),
    sgptNormal: getParameterNormal('SGPT (ALT)'),
    sgptUnit: getParameterUnit('SGPT (ALT)'),
    sgot: getParameterValue('SGOT (AST)'),
    sgotNormal: getParameterNormal('SGOT (AST)'),
    sgotUnit: getParameterUnit('SGOT (AST)'),
    alp: getParameterValue('ALP (Alkaline Phosphatase)'),
    alpNormal: getParameterNormal('ALP (Alkaline Phosphatase)'),
    alpUnit: getParameterUnit('ALP (Alkaline Phosphatase)'),
    // กรดยูริค - หน้า 3
    uricAcid: getParameterValue('Uric Acid'),
    uricAcidNormal: getParameterNormal('Uric Acid'),
    uricAcidUnit: getParameterUnit('Uric Acid'),
    // ผลการตรวจปัสสาวะเพิ่มเติม - หน้า 4
    urineColor: getParameterValue('Color'),
    urineColorNormal: getParameterNormal('Color'),
    urineColorUnit: getParameterUnit('Color'),
    urinePh: getParameterValue('pH'),
    urinePhNormal: getParameterNormal('pH'),
    urinePhUnit: getParameterUnit('pH'),
    urineSpecificGravity: getParameterValue('Specific Gravity'),
    urineSpecificGravityNormal: getParameterNormal('Specific Gravity'),
    urineSpecificGravityUnit: getParameterUnit('Specific Gravity'),
    urineKetones: getParameterValue('Ketones'),
    urineKetonesNormal: getParameterNormal('Ketones'),
    urineKetonesUnit: getParameterUnit('Ketones'),
    urineBlood: getParameterValue('Blood'),
    urineBloodNormal: getParameterNormal('Blood'),
    urineBloodUnit: getParameterUnit('Blood'),
    urineWbc: getParameterValue('Leukocytes'),
    urineWbcNormal: getParameterNormal('Leukocytes'),
    urineWbcUnit: getParameterUnit('Leukocytes'),
    urineNitrite: getParameterValue('Nitrite'),
    urineNitriteNormal: getParameterNormal('Nitrite'),
    urineNitriteUnit: getParameterUnit('Nitrite'),
    urineBilirubin: getParameterValue('Bilirubin'),
    urineBilirubinNormal: getParameterNormal('Bilirubin'),
    urineBilirubinUnit: getParameterUnit('Bilirubin'),
    urineUrobilinogen: getParameterValue('Urobilinogen'),
    urineUrobilinogenNormal: getParameterNormal('Urobilinogen'),
    urineUrobilinogenUnit: getParameterUnit('Urobilinogen'),
    urineRbc: getParameterValue('RBC (Urine)'),
    urineRbcNormal: getParameterNormal('RBC (Urine)'),
    urineRbcUnit: getParameterUnit('RBC (Urine)'),
    urineWbcMicroscopic: getParameterValue('WBC (Urine)'),
    urineWbcMicroscopicNormal: getParameterNormal('WBC (Urine)'),
    urineWbcMicroscopicUnit: getParameterUnit('WBC (Urine)')
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrint = () => {
    if (isApproved) {
      window.print();
    }
  };

  return (
    <>
      <PrintStyles />
      <ModalOverlay onClick={handleOverlayClick}>
        <PrintButton onClick={handlePrint} disabled={!isApproved}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9"></polyline>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
            <rect x="6" y="14" width="12" height="8"></rect>
          </svg>
          Print
        </PrintButton>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalContent id="health-report-modal">
        <ReportContainer>
          {/* หน้าที่ 1 */}
          <PageNumber>1</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          <ContentSection>
            <LeftSection>
              <HumanImage src="/human.png" alt="Human Body" />
            </LeftSection>

            <RightSection>
              <ResultHeader>
                ผลการตรวจสุขภาพร่างกายทั่วไป
              </ResultHeader>
              <ResultTable>
                <ResultRow>
                  <ResultLabel>น้ำหนัก (Weight)</ResultLabel>
                  <ResultValue>{data.weight}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ส่วนสูง (Height)</ResultLabel>
                  <ResultValue>{data.height}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ดัชนีมวลกาย (BMI)</ResultLabel>
                  <ResultValue>{data.bmi}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>• ผลการประเมิน BMI</ResultLabel>
                  <ResultValue>
                    <StatusBadge>{data.bmiStatus}</StatusBadge>
                  </ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ความดันโลหิต (BP)</ResultLabel>
                  <ResultValue>{data.bloodPressure}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ชีพจร (Pulse)</ResultLabel>
                  <ResultValue>{data.pulse}</ResultValue>
                </ResultRow>
                <ResultRowFull>
                  <ResultFullTitle>โรคประจำตัว</ResultFullTitle>
                  <ResultFullContent>• {data.diseases}</ResultFullContent>
                </ResultRowFull>
                <ResultRowFull>
                  <ResultFullTitle>ประวัติแพ้ยา ,แพ้อาหาร</ResultFullTitle>
                  <ResultFullContent>• {data.foodAllergies}</ResultFullContent>
                </ResultRowFull>
              </ResultTable>
            </RightSection>
          </ContentSection>

          <AdditionalSection>
            {/* สรุปความเสี่ยงสุขภาพ - แสดง tags */}
            {data.tags && data.tags.length > 0 && (
              <InfoBox>
                <InfoBoxHeader>
                  <InfoIcon src="/ic_1.png" alt="Health Risk Icon" />
                  <InfoBoxTitle>สรุปความเสี่ยงสุขภาพ</InfoBoxTitle>
                </InfoBoxHeader>
                <ButtonGroup>
                  {data.tags.map((tag, index) => (
                    <RiskButton key={index}>{tag}</RiskButton>
                  ))}
                </ButtonGroup>
              </InfoBox>
            )}

            {/* คำแนะนำจากแพทย์ */}
            {data.doctorNote && data.doctorNote !== '-' && (
              <InfoBox>
                <InfoBoxHeader>
                  <InfoIcon src="/ic_2.png" alt="Doctor Advice Icon" />
                  <InfoBoxTitle>คำแนะนำจากแพทย์</InfoBoxTitle>
                </InfoBoxHeader>
                <InfoBoxContent style={{ whiteSpace: 'pre-wrap' }}>
                  {data.doctorNote}
                </InfoBoxContent>
              </InfoBox>
            )}

            {/* คำแนะนำด้านโภชนาการ */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Nutrition Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านโภชนาการ</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent style={{ whiteSpace: 'pre-wrap' }}>
                {data.nutritionAdvice || ''}
              </InfoBoxContent>
            </InfoBox>
          </AdditionalSection>

          <PageBreak />

          {/* หน้าที่ 2 */}
          <PageNumber>2</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          <AdditionalSection>
            {/* คำแนะนำด้านการออกกำลังกาย */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Exercise Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านการออกกำลังกาย</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent style={{ whiteSpace: 'pre-wrap' }}>
                {data.exerciseAdvice || ''}
              </InfoBoxContent>
            </InfoBox>

            {/* คำแนะนำด้านการนอน */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Sleep Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านการนอน</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent style={{ whiteSpace: 'pre-wrap' }}>
                {data.sleepAdvice || ''}
              </InfoBoxContent>
            </InfoBox>
          </AdditionalSection>

          {/* ตารางความสมบูรณ์ของเม็ดเลือด */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ความสมบูรณ์ของเม็ดเลือด</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เม็ดเลือดขาว (WBC)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.wbc, data.wbcNormal, data.wbcUnit)}>{data.wbc}</BloodTestCell>
                <BloodTestCell center>{data.wbcNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ฮีโมโกลบิน (Hemoglobin)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.hemoglobin, data.hemoglobinNormal, data.hemoglobinUnit)}>{data.hemoglobin}</BloodTestCell>
                <BloodTestCell center>{data.hemoglobinNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ฮีมาโตคริต (Hematocrit)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.hematocrit, data.hematocritNormal, data.hematocritUnit)}>{data.hematocrit}</BloodTestCell>
                <BloodTestCell center>{data.hematocritNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เกล็ดเลือด (Platelet)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.platelet, data.plateletNormal, data.plateletUnit)}>{data.platelet}</BloodTestCell>
                <BloodTestCell center>{data.plateletNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>นิวโทรฟิล (Neutrophile)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.neutrophile, data.neutrophileNormal, data.neutrophileUnit)}>{data.neutrophile}</BloodTestCell>
                <BloodTestCell center>{data.neutrophileNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ลิมโฟไซต์ (Lymphocyte)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.lymphocyte, data.lymphocyteNormal, data.lymphocyteUnit)}>{data.lymphocyte}</BloodTestCell>
                <BloodTestCell center>{data.lymphocyteNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>โมโนไซต์ (Monocyte)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.monocyte, data.monocyteNormal, data.monocyteUnit)}>{data.monocyte}</BloodTestCell>
                <BloodTestCell center>{data.monocyteNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางการตรวจปัสสาวะ */}
          <BloodTestTableWrapper>
            <BloodTestHeader>การตรวจปัสสาวะ (Urinalysis)</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>น้ำตาลในปัสสาวะ (Urine Sugar)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineSugar, data.urineSugarNormal, data.urineSugarUnit)}>{data.urineSugar}</BloodTestCell>
                <BloodTestCell center>{data.urineSugarNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>โปรตีนในปัสสาวะ (Urine Protein)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineProtein, data.urineProteinNormal, data.urineProteinUnit)}>{data.urineProtein}</BloodTestCell>
                <BloodTestCell center>{data.urineProteinNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          <PageBreak />

          {/* หน้าที่ 3 */}
          <PageNumber>3</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          {/* ตารางไขมันในเลือด */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ไขมันในเลือด</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอล</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.totalCholesterol, data.totalCholesterolNormal, data.totalCholesterolUnit)}>{data.totalCholesterol}</BloodTestCell>
                <BloodTestCell center>{data.totalCholesterolNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ไตรกลีเซอไรด์</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.triglyceride, data.triglycerideNormal, data.triglycerideUnit)}>{data.triglyceride}</BloodTestCell>
                <BloodTestCell center>{data.triglycerideNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอลดี (HDL)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.hdl, data.hdlNormal, data.hdlUnit)}>{data.hdl}</BloodTestCell>
                <BloodTestCell center>{data.hdlNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอลไม่ดี (LDL)</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.ldl, data.ldlNormal, data.ldlUnit)}>{data.ldl}</BloodTestCell>
                <BloodTestCell center>{data.ldlNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางการทำงานของไต */}
          <BloodTestTableWrapper>
            <BloodTestHeader>การทำงานของไต</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Creatinine</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.creatinine, data.creatinineNormal, data.creatinineUnit)}>{data.creatinine}</BloodTestCell>
                <BloodTestCell center>{data.creatinineNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>BUN , Urea</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.bun, data.bunNormal, data.bunUnit)}>{data.bun}</BloodTestCell>
                <BloodTestCell center>{data.bunNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>eGFR</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.egfr, data.egfrNormal, data.egfrUnit)}>{data.egfr}</BloodTestCell>
                <BloodTestCell center>{data.egfrNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางการทำงานของตับ */}
          <BloodTestTableWrapper>
            <BloodTestHeader>การทำงานของตับ</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>SGPT</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.sgpt, data.sgptNormal, data.sgptUnit)}>{data.sgpt}</BloodTestCell>
                <BloodTestCell center>{data.sgptNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>SGOT</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.sgot, data.sgotNormal, data.sgotUnit)}>{data.sgot}</BloodTestCell>
                <BloodTestCell center>{data.sgotNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Alkaline Phosphatase</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.alp, data.alpNormal, data.alpUnit)}>{data.alp}</BloodTestCell>
                <BloodTestCell center>{data.alpNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางกรดยูริค */}
          <BloodTestTableWrapper>
            <BloodTestHeader>กรดยูริค</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Uric Acid</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.uricAcid, data.uricAcidNormal, data.uricAcidUnit)}>{data.uricAcid}</BloodTestCell>
                <BloodTestCell center>{data.uricAcidNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          <PageBreak />

          {/* หน้าที่ 4 */}
          <PageNumber>4</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          {/* ตารางผลการตรวจปัสสาวะ */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ผลการตรวจปัสสาวะ</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>สีของปัสสาวะ</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineColor, data.urineColorNormal, data.urineColorUnit)}>{data.urineColor}</BloodTestCell>
                <BloodTestCell center>{data.urineColorNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>pH</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urinePh, data.urinePhNormal, data.urinePhUnit)}>{data.urinePh}</BloodTestCell>
                <BloodTestCell center>{data.urinePhNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ความถ่วงจำเพาะ</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineSpecificGravity, data.urineSpecificGravityNormal, data.urineSpecificGravityUnit)}>{data.urineSpecificGravity}</BloodTestCell>
                <BloodTestCell center>{data.urineSpecificGravityNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>โปรตีน</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineProtein, data.urineProteinNormal, data.urineProteinUnit)}>{data.urineProtein}</BloodTestCell>
                <BloodTestCell center>{data.urineProteinNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>น้ำตาล</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineSugar, data.urineSugarNormal, data.urineSugarUnit)}>{data.urineSugar}</BloodTestCell>
                <BloodTestCell center>{data.urineSugarNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คีโตน</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineKetones, data.urineKetonesNormal, data.urineKetonesUnit)}>{data.urineKetones}</BloodTestCell>
                <BloodTestCell center>{data.urineKetonesNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เลือด</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineBlood, data.urineBloodNormal, data.urineBloodUnit)}>{data.urineBlood}</BloodTestCell>
                <BloodTestCell center>{data.urineBloodNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เม็ดเลือดขาว</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineWbc, data.urineWbcNormal, data.urineWbcUnit)}>{data.urineWbc}</BloodTestCell>
                <BloodTestCell center>{data.urineWbcNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ไนเตรต</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineNitrite, data.urineNitriteNormal, data.urineNitriteUnit)}>{data.urineNitrite}</BloodTestCell>
                <BloodTestCell center>{data.urineNitriteNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>บิลิรูบิน</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineBilirubin, data.urineBilirubinNormal, data.urineBilirubinUnit)}>{data.urineBilirubin}</BloodTestCell>
                <BloodTestCell center>{data.urineBilirubinNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ยูโรบิลินโนเจน</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineUrobilinogen, data.urineUrobilinogenNormal, data.urineUrobilinogenUnit)}>{data.urineUrobilinogen}</BloodTestCell>
                <BloodTestCell center>{data.urineUrobilinogenNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เซลล์เม็ดเลือดแดง</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineRbc, data.urineRbcNormal, data.urineRbcUnit)}>{data.urineRbc}</BloodTestCell>
                <BloodTestCell center>{data.urineRbcNormal}</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เซลล์เม็ดเลือดขาว</BloodTestCell>
                <BloodTestCell center abnormal={isAbnormal(data.urineWbcMicroscopic, data.urineWbcMicroscopicNormal, data.urineWbcMicroscopicUnit)}>{data.urineWbcMicroscopic}</BloodTestCell>
                <BloodTestCell center>{data.urineWbcMicroscopicNormal}</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>
        </ReportContainer>
      </ModalContent>
    </ModalOverlay>
    </>
  );
};

export default HealthReportModal;
